import i18n, { changeLanguage as i18nChangeLanguage } from 'i18next';
import { initReactI18next } from 'react-i18next';

import en from './locales/en.json';

const languageLoaders: Record<string, () => Promise<any>> = {
  es: () => import('./locales/es.json'),
  zh: () => import('./locales/zh.json'),
  hi: () => import('./locales/hi.json'),
  fr: () => import('./locales/fr.json'),
  'fr-CA': () => import('./locales/fr-CA.json'),
  pt: () => import('./locales/pt.json'),
  ru: () => import('./locales/ru.json'),
  bn: () => import('./locales/bn.json'),
  vi: () => import('./locales/vi.json'),
  tl: () => import('./locales/tl.json'),
  de: () => import('./locales/de.json'),
  ko: () => import('./locales/ko.json'),
  ja: () => import('./locales/ja.json'),
  it: () => import('./locales/it.json'),
  ta: () => import('./locales/ta.json'),
  te: () => import('./locales/te.json'),
  ht: () => import('./locales/ht.json'),
  pa: () => import('./locales/pa.json'),
  my: () => import('./locales/my.json'),
  uk: () => import('./locales/uk.json'),
};

const LANGUAGE_STORAGE_KEY = 'acnt_language';

const savedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY) ?? 'en';

i18n
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
    },
    lng: 'en',
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false,
    },
  });

if (savedLanguage !== 'en') {
  void loadAndChangeLanguage(savedLanguage);
}

/**
 * Loads a language bundle if not already loaded, then changes to it
 */
export async function loadAndChangeLanguage(lang: string): Promise<void> {
  try {
    if (i18n.hasResourceBundle(lang, 'translation')) {
      await i18nChangeLanguage(lang);
      localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
      return;
    }

    const loader = languageLoaders[lang];
    if (!loader) {
      console.warn(`No loader found for language: ${lang}. Falling back to English.`);
      await i18nChangeLanguage('en');
      localStorage.setItem(LANGUAGE_STORAGE_KEY, 'en');
      return;
    }

    const module = await loader();
    const translations = module.default || module;
    i18n.addResourceBundle(lang, 'translation', translations, true, true);

    await i18nChangeLanguage(lang);
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
  } catch (error) {
    console.error(`Failed to load language: ${lang}`, error);
    await i18nChangeLanguage('en');
    localStorage.setItem(LANGUAGE_STORAGE_KEY, 'en');
  }
}

export const changeLanguage = (lang: string): void => {
  void loadAndChangeLanguage(lang);
};

export const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'zh', label: '简体中文' },
  { code: 'hi', label: 'हिन्दी' },
  { code: 'fr', label: 'Français' },
  { code: 'fr-CA', label: 'Français (CA)' },
  { code: 'pt', label: 'Português' },
  { code: 'ru', label: 'Русский' },
  { code: 'bn', label: 'বাংলা' },
  { code: 'vi', label: 'Tiếng Việt' },
  { code: 'tl', label: 'Tagalog' },
  { code: 'de', label: 'Deutsch' },
  { code: 'ko', label: '한국어' },
  { code: 'ja', label: '日本語' },
  { code: 'it', label: 'Italiano' },
  { code: 'ta', label: 'தமிழ்' },
  { code: 'te', label: 'తెలుగు' },
  { code: 'ht', label: 'Kreyòl' },
  { code: 'pa', label: 'ਪੰਜਾਬੀ' },
  { code: 'my', label: 'မြန်မာစာ' },
  { code: 'uk', label: 'Українська' },
] as const;

export default i18n;
